import React, { useEffect, useRef, useState, useCallback } from "react";
import styled from "styled-components";

import { DEV_HOSTS } from "../constants/hosts";

const Wrapper = styled.div<{ $dragging: boolean }>`
	position: fixed;
	z-index: 9999;
	background: #121212;
	color: #fff;
	padding: 10px 14px;
	border-radius: 10px;
	font-size: 13px;
	font-family: monospace;
	box-shadow: 0 0 8px rgba(0, 0, 0, 0.3);
	opacity: 0.95;
	min-width: 220px;
	line-height: 1.7;
	cursor: ${({ $dragging }) => ($dragging ? "grabbing" : "grab")};
	user-select: none;
`;

const Row = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
`;

const Value = styled.b<{ color?: string }>`
	color: ${({ color }) => color || "#fff"};
`;

const Chart = styled.canvas`
	width: 100%;
	height: 60px;
	margin-top: 10px;
	border: 1px solid #333;
	border-radius: 4px;
`;

const DevToolbar: React.FC = () => {
	const [fps, setFps] = useState(0);
	const [avgFps, setAvgFps] = useState(0);
	const [domCount, setDomCount] = useState(0);
	const [heap, setHeap] = useState<number | null>(null);

	const [dragging, setDragging] = useState(false);
	const [position, setPosition] = useState({
		x: 10,
		y: 10,
	});

	const wrapperRef = useRef<HTMLDivElement>(null);
	const chartRef = useRef<HTMLCanvasElement>(null);

	const frame = useRef(0);
	const last = useRef(performance.now());
	const fpsHistory = useRef<number[]>([]);
	const heapHistory = useRef<number[]>([]);

	const isLocalhost = DEV_HOSTS.includes(window.location.hostname);
	const isDev = process.env.NODE_ENV === "development";

	useEffect(() => {
		if (!isDev || !isLocalhost) return undefined;

		const updateDomCount = () => {
			setDomCount(document.getElementsByTagName("*").length);
		};

		const updateHeap = () => {
			if ((performance as any)?.memory) {
				const { usedJSHeapSize } = (performance as any).memory;
				const value = usedJSHeapSize / 1024 / 1024;
				setHeap(value);
				heapHistory.current.push(value);
				if (heapHistory.current.length > 20)
					heapHistory.current.shift();
			}
		};

		const updateFPS = () => {
			const now = performance.now();
			frame.current++;
			if (now >= last.current + 1000) {
				const currentFps = frame.current;
				setFps(currentFps);

				fpsHistory.current.push(currentFps);
				if (fpsHistory.current.length > 20) fpsHistory.current.shift();

				const avg =
					fpsHistory.current.reduce((a, b) => a + b, 0) /
					fpsHistory.current.length;
				setAvgFps(Math.round(avg));

				drawChart(avg);

				frame.current = 0;
				last.current = now;
			}
			requestAnimationFrame(updateFPS);
		};

		updateDomCount();
		updateHeap();
		updateFPS();

		const interval = setInterval(() => {
			updateDomCount();
			updateHeap();
		}, 2000);

		return () => clearInterval(interval);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const handleMouseMove = (e: MouseEvent) => {
			if (!dragging) return;
			const offsetX = 100;
			const offsetY = 20;
			setPosition({
				x: e.clientX - offsetX,
				y: window.innerHeight - e.clientY - offsetY,
			});
		};

		const handleMouseUp = () => setDragging(false);
		window.addEventListener("mousemove", handleMouseMove);
		window.addEventListener("mouseup", handleMouseUp);
		return () => {
			window.removeEventListener("mousemove", handleMouseMove);
			window.removeEventListener("mouseup", handleMouseUp);
		};
	}, [dragging]);

	useEffect(() => {
		const handleResize = () => {
			setPosition((pos) => ({
				...pos,
				y: Math.min(pos.y, window.innerHeight - 80),
			}));
		};
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	const drawChart = (avg: number) => {
		const canvas = chartRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		const { width, height } = canvas;
		ctx.clearRect(0, 0, width, height);
		ctx.fillStyle = "#1e1e1e";
		ctx.fillRect(0, 0, width, height);

		ctx.strokeStyle = "#444";
		ctx.lineWidth = 1;
		ctx.font = "10px monospace";
		ctx.fillStyle = "#888";

		[30, 60, 90, 120].forEach((mark) => {
			const y = height - (mark / 120) * height;
			ctx.beginPath();
			ctx.moveTo(0, y);
			ctx.lineTo(width, y);
			ctx.stroke();
			ctx.fillText(`${mark}`, 2, y - 2);
		});

		const step = width / (fpsHistory.current.length - 1);
		ctx.fillStyle = "#888";
		for (let i = 5; i < fpsHistory.current.length; i += 5) {
			const x = i * step;

			const label = `${i}s`;
			ctx.fillText(label, x, height - 2);
			ctx.beginPath();
			ctx.moveTo(x, height);
			ctx.lineTo(x, height - 4);
			ctx.stroke();
		}

		const drawLine = (data: number[], color: string) => {
			if (data.length < 2) return;
			ctx.beginPath();
			for (let i = 0; i < data.length; i++) {
				const x = i * step;
				const y = height - (data[i] / 120) * height;
				if (i === 0) ctx.moveTo(x, y);
				else ctx.lineTo(x, y);
			}
			ctx.strokeStyle = color;
			ctx.lineWidth = 2;
			ctx.stroke();
		};

		drawLine(fpsHistory.current, "rgba(76, 175, 80, 0.85)");
		const avgArray = new Array(fpsHistory.current.length).fill(avg);
		drawLine(avgArray, "rgba(255, 235, 59, 0.85)");
	};

	const getFpsColor = useCallback((value: number) => {
		if (value > 50) return "#4caf50";
		if (value > 30) return "#ff9800";
		return "#f44336";
	}, []);

	if (!isDev || !isLocalhost) return null;

	return (
		<Wrapper
			ref={wrapperRef}
			$dragging={dragging}
			style={{ left: position.x, bottom: position.y }}
			onMouseDown={() => setDragging(true)}
		>
			<Row>
				<span>🧱 DOM Nodes:</span>
				<Value>{domCount}</Value>
			</Row>
			<Row>
				<span>🎮 FPS:</span>
				<Value color={getFpsColor(fps)}>{fps}</Value>
			</Row>
			<Row>
				<span>📊 Avg FPS (2m):</span>
				<Value color={getFpsColor(avgFps)}>{avgFps}</Value>
			</Row>
			<Row>
				<span>🧠 JS Heap:</span>
				<Value>{heap !== null ? `${heap.toFixed(1)} MB` : "N/A"}</Value>
			</Row>
			<Chart ref={chartRef} width={180} height={60} />
		</Wrapper>
	);
};

export default DevToolbar;
